'use client';

import { useAppStore } from '@/lib/stores/app-store';
import { GraduationCap, Globe, Clock, Trash2, Play } from 'lucide-react';
import { motion } from 'framer-motion';

export function WelcomeScreen() {
  const {
    language,
    setLanguage,
    setScreen,
    savedSessions,
    loadSession,
    deleteSession,
  } = useAppStore();

  const isHebrew = language === 'he';

  const text = {
    en: {
      title: 'TeachMe',
      subtitle: 'Learn anything with an AI tutor',
      description: 'Your personal Socratic teacher that guides you through any subject with questions, not answers.',
      start: "Start New Session",
      langLabel: 'Language',
      recentSessions: 'Recent Sessions',
      resume: 'Resume',
      noSessions: 'No saved sessions yet',
      messagesCount: 'messages',
    },
    he: {
      title: 'למד אותי',
      subtitle: 'למד כל דבר עם מורה AI',
      description: 'המורה הסוקרטי האישי שלך שמנחה אותך בכל נושא באמצעות שאלות, לא תשובות.',
      start: 'התחל שיעור חדש',
      langLabel: 'שפה',
      recentSessions: 'שיעורים אחרונים',
      resume: 'המשך',
      noSessions: 'אין שיעורים שמורים',
      messagesCount: 'הודעות',
    },
  };

  const t = text[language];

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else if (diffDays === 1) {
      return language === 'he' ? 'אתמול' : 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', {
        weekday: 'long',
      });
    } else {
      return date.toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8"
      dir={isHebrew ? 'rtl' : 'ltr'}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full"
      >
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary flex items-center justify-center">
            <GraduationCap className="w-10 h-10 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-bold mb-2">{t.title}</h1>
          <p className="text-lg text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Description */}
        <p className="text-muted-foreground mb-8 leading-relaxed text-center">
          {t.description}
        </p>

        {/* Language Toggle */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <Globe className="w-5 h-5 text-muted-foreground" />
          <div className="flex rounded-full overflow-hidden border border-border">
            <button
              onClick={() => setLanguage('en')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                language === 'en'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card hover:bg-muted'
              }`}
            >
              English
            </button>
            <button
              onClick={() => setLanguage('he')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                language === 'he'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card hover:bg-muted'
              }`}
            >
              עברית
            </button>
          </div>
        </div>

        {/* Start New Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setScreen('select')}
          className="btn btn-primary w-full text-lg py-4 mb-8"
        >
          {t.start}
        </motion.button>

        {/* Saved Sessions */}
        {savedSessions.length > 0 && (
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{t.recentSessions}</span>
            </div>
            <div className="max-h-64 overflow-auto">
              {savedSessions.slice(0, 5).map((session) => (
                <div
                  key={session.id}
                  className="px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{session.name}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <span>{formatDate(session.updatedAt)}</span>
                        <span>•</span>
                        <span>{session.messages.length} {t.messagesCount}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => loadSession(session.id)}
                        className="btn btn-primary px-3 py-1.5 text-sm"
                      >
                        <Play className="w-3 h-3" />
                        {t.resume}
                      </button>
                      <button
                        onClick={() => deleteSession(session.id)}
                        className="p-2 text-muted-foreground hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
